class SidebarMenu extends HTMLElement {
  connectedCallback() {
    this.openButton = document.querySelector('.sidebar_open_button');
    this.backdrop = this.nextElementSibling;
    this.tabTriggers = [...this.querySelectorAll('.sidebar_tab_trigger')];
    this.tabs = [...this.querySelectorAll('.sidebar_tab')];

    if (!this.openButton) {
      console.warn('SidebarMenu: Missing required elements');
      return;
    }

    this.setAttribute('aria-hidden', 'true');
    this.setAttribute('inert', '');

    this.openButton.addEventListener('click', this.toggleSidebar.bind(this));
    this.backdrop.addEventListener('click', this.closeSidebar.bind(this));
    this.addEventListener('keydown', this._handleKeydown.bind(this));

    this.tabTriggers.forEach((trigger) => {
      trigger.addEventListener('click', () => this._openTab(trigger));
    });

    this._handleTabSwipes();
  }

  // --- Sidebar Control ---

  toggleSidebar() {
    this.classList.contains('open') ? this.closeSidebar() : this.openSidebar();
  }

  openSidebar() {
    this.classList.add('open');
    this._updateSidebarState(true);
  }

  closeSidebar() {
    if (!this.classList.contains('open')) return;

    // Keeps the header colour swap instant on close, like HeaderMega.closeDropdown.
    const headerInner = document.querySelector('.header-inner');
    headerInner?.classList.add('sidebar-closing');

    this.classList.remove('open');
    this._updateSidebarState(false);
    this.openButton.focus();
    setTimeout(() => this._resetAccordions(), 400);

    if (headerInner) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => headerInner.classList.remove('sidebar-closing'));
      });
    }
  }

  _updateSidebarState(isOpen) {
    document.body.classList.toggle('overflow-hidden', isOpen);
    this.setAttribute('aria-hidden', (!isOpen).toString());
    this.toggleAttribute('inert', !isOpen);
    this.openButton.setAttribute('aria-expanded', isOpen.toString());
    this.openButton.setAttribute('aria-label', isOpen ? 'Close sidebar' : 'Open sidebar');

    if (isOpen) {
      setTimeout(() => trapFocus(this, this.tabTriggers[0] || this), 300);
    }
  }

  _handleKeydown(event) {
    if (event.key === 'Escape') this.closeSidebar();
  }

  // --- Top-level tabs ---

  _openTab(trigger) {
    if (trigger.classList.contains('is-active')) return;
    const targetId = trigger.getAttribute('aria-controls');

    this.tabTriggers.forEach((otherTrigger) => {
      const isTarget = otherTrigger === trigger;
      otherTrigger.classList.toggle('is-active', isTarget);
      otherTrigger.setAttribute('aria-expanded', isTarget.toString());
    });

    this.tabs.forEach((tab) => {
      tab.hidden = tab.id !== targetId;
    });

    const target = this.tabs.find((tab) => tab.id === targetId);
    target.querySelectorAll('accordion-element').forEach((accordion) => accordion.refreshHeight());
  }

  // Horizontal swipe only (50px, 1.5x vertical) so accordion scroll is not read as a swipe.
  _handleTabSwipes() {
    const mediaQuery = window.matchMedia('(max-width: 1024px)');
    this.tabs.forEach((tab) => {
      let startX = 0;
      let startY = 0;
      tab.addEventListener('touchstart', (event) => {
        startX = event.touches[0].clientX;
        startY = event.touches[0].clientY;
      });
      tab.addEventListener('touchend', (event) => {
        if (!mediaQuery.matches) return;
        const deltaX = event.changedTouches[0].clientX - startX;
        const deltaY = event.changedTouches[0].clientY - startY;
        const minDistance = 50;
        if (Math.abs(deltaX) < minDistance || Math.abs(deltaX) < 1.5 * Math.abs(deltaY)) return;

        const activeIndex = this.tabTriggers.findIndex((trigger) => trigger.classList.contains('is-active'));
        if (activeIndex === -1) return;
        const nextTrigger = deltaX < 0 ? this.tabTriggers[activeIndex + 1] : this.tabTriggers[activeIndex - 1];
        if (nextTrigger) this._openTab(nextTrigger);
      });
    });
  }

  // --- Accordions ---

  _resetAccordions() {
    this.tabs.forEach((tab) => {
      [...tab.querySelectorAll('accordion-element')].forEach((accordion, index) => {
        index === 0 ? accordion.open() : accordion.close();
      });
    });
  }
}

customElements.define('sidebar-menu', SidebarMenu);
